import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, X, Gift, ChevronRight, Calendar, ChevronDown } from 'lucide-react';
import { defaultData } from '../utils/storage';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const daysInMonth = (month, year) => new Date(year || 2020, month, 0).getDate();

const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Parse "8", "08", "Aug", "August" -> 1..12 or null
const parseMonthInput = (str) => {
  if (!str) return null;
  const trimmed = String(str).trim();
  if (!trimmed) return null;
  const num = parseInt(trimmed, 10);
  if (!isNaN(num) && num >= 1 && num <= 12) return num;
  const lower = trimmed.toLowerCase();
  const idx = monthNames.findIndex((m) => m.toLowerCase().startsWith(lower));
  return idx >= 0 ? idx + 1 : null;
};

// Backward compat: legacy `{ birthDate: 'YYYY-MM-DD' }` -> `{ day, month, year }`
const normalizePerson = (p) => {
  if (p.day && p.month) return p;
  if (p.birthDate) {
    const [y, m, d] = p.birthDate.split('-').map(Number);
    return { ...p, day: d, month: m, year: y };
  }
  return p;
};

const validateForm = (form, currentYear) => {
  if (!form.name.trim()) return false;
  const m = parseMonthInput(form.monthText);
  if (!m) return false;
  const d = Number(form.day);
  if (!d || d < 1 || d > daysInMonth(m, form.year ? Number(form.year) : null)) return false;
  if (form.year !== '' && form.year != null) {
    const y = Number(form.year);
    if (!y || y < 1900 || y > currentYear) return false;
  }
  return true;
};

// ---------------- PersonForm (top-level so inputs keep focus) ----------------
const PersonForm = ({ form, setForm, onSubmit, onCancel, onDelete, submitLabel, currentYear }) => {
  const nameRef = useRef(null);
  useEffect(() => {
    if (nameRef.current) nameRef.current.focus();
  }, []);

  const monthNum = parseMonthInput(form.monthText);
  const yearNum = form.year ? Number(form.year) : null;
  const maxDay = monthNum ? daysInMonth(monthNum, yearNum) : 31;

  const handleMonthBlur = () => {
    const parsed = parseMonthInput(form.monthText);
    if (parsed) {
      setForm({ ...form, monthText: monthNames[parsed - 1] });
    }
  };

  const isValid = validateForm(form, currentYear);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isValid) onSubmit();
      }}
      className="space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          ref={nameRef}
          type="text"
          className="input w-full"
          placeholder="e.g., Lisa"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Birth Date</label>
        <div className="grid grid-cols-[1fr_1.5fr_1fr] gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Day</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="DD"
              className="input w-full text-center"
              value={form.day}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                setForm({ ...form, day: v });
              }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
            <input
              type="text"
              placeholder="MM or name"
              className="input w-full"
              value={form.monthText}
              onChange={(e) => setForm({ ...form, monthText: e.target.value })}
              onBlur={handleMonthBlur}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Year <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="YYYY"
              className="input w-full text-center"
              value={form.year}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setForm({ ...form, year: v });
              }}
            />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Tab through fields. Month accepts <span className="font-medium">08</span> or <span className="font-medium">Aug</span>. Press Enter to save.
        </p>
        {form.day && monthNum && Number(form.day) > maxDay && (
          <p className="text-xs text-red-500 mt-1">
            {monthNames[monthNum - 1]} only has {maxDay} days.
          </p>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={!isValid}
          className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitLabel}
        </button>
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="btn bg-red-100 text-red-600 hover:bg-red-200 flex-1"
          >
            Delete
          </button>
        ) : (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

// ---------------- Main component ----------------
// When `sharedCalendarId` is provided, this component manages birthdays that
// belong to that shared calendar instead of the personal list. Birthday
// display settings (linkedToCalendar, categoryId) then come from the shared
// calendar's own columns rather than the global `birthdaySettings` singleton.
const BirthdayCalendar = ({ data, updateData, sharedCalendarId = null, onBack = null }) => {
  const allBirthdays = data.calendar?.birthdays || [];
  const rawBirthdays = useMemo(
    () =>
      allBirthdays.filter((b) =>
        sharedCalendarId
          ? b.sharedCalendarId === sharedCalendarId
          : !b.sharedCalendarId
      ),
    [allBirthdays, sharedCalendarId]
  );
  const birthdays = useMemo(() => rawBirthdays.map(normalizePerson), [rawBirthdays]);

  // Category list scoped to the current calendar (personal vs shared).
  // Shared calendars don't persist the 13 default color rows, so overlay
  // them on top of any per-calendar custom renames — same approach as the
  // Calendar grid — to keep the label picker populated.
  const allCategories = data.calendar?.categories || [];
  const categories = useMemo(() => {
    const scoped = allCategories.filter((c) =>
      sharedCalendarId
        ? c.sharedCalendarId === sharedCalendarId
        : !c.sharedCalendarId
    );
    if (!sharedCalendarId) return scoped;
    const defaults = defaultData.calendar.categories;
    const byId = new Map(scoped.map((c) => [c.id, c]));
    return defaults.map((d) => byId.get(d.id) || { ...d, sharedCalendarId });
  }, [allCategories, sharedCalendarId]);

  const sharedCalendars = data.calendar?.sharedCalendars || [];
  const sharedCal = sharedCalendarId
    ? sharedCalendars.find((c) => c.id === sharedCalendarId) || null
    : null;
  const personalBirthdaySettings = data.calendar?.birthdaySettings || {
    linkedToCalendar: false,
    categoryId: 'purple'
  };
  const birthdaySettings = sharedCal
    ? {
        linkedToCalendar: sharedCal.birthdayLinked !== false,
        categoryId: sharedCal.birthdayCategoryId || 'purple'
      }
    : personalBirthdaySettings;

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const emptyForm = { name: '', day: '', monthText: '', year: '' };
  const [newPerson, setNewPerson] = useState(emptyForm);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const selectedCategory = categories.find((c) => c.id === birthdaySettings.categoryId) || categories[0];

  const updateSettings = (patch) => {
    if (sharedCal) {
      // Shared calendar: patch the shared_calendars row via a direct update
      // (RLS restricts to owner). Optimistically mirror in local state so
      // the UI feels responsive; polling/reload will reconcile.
      const patched = {
        ...sharedCal,
        birthdayLinked: patch.linkedToCalendar ?? sharedCal.birthdayLinked,
        birthdayCategoryId: patch.categoryId ?? sharedCal.birthdayCategoryId
      };
      const nextShared = sharedCalendars.map((c) => (c.id === sharedCal.id ? patched : c));
      updateData({
        calendar: { ...data.calendar, sharedCalendars: nextShared }
      });
      // Fire-and-forget DB write.
      import('../lib/sharedCalendars').then(({ updateSharedCalendarBirthdaySettings }) =>
        updateSharedCalendarBirthdaySettings(sharedCal.id, {
          birthdayLinked: patched.birthdayLinked,
          birthdayCategoryId: patched.birthdayCategoryId
        }).catch(() => {})
      );
      return;
    }
    updateData({
      calendar: {
        ...data.calendar,
        birthdaySettings: { ...birthdaySettings, ...patch }
      }
    });
  };

  // Persist full birthday list while preserving rows from other calendar
  // scopes (personal vs shared-per-calendar).
  const commitBirthdays = (list) => {
    const otherScope = allBirthdays.filter((b) =>
      sharedCalendarId
        ? b.sharedCalendarId !== sharedCalendarId
        : !!b.sharedCalendarId
    );
    // Tag every row we own with the current scope.
    const tagged = list.map((b) => ({
      ...b,
      sharedCalendarId: sharedCalendarId || null
    }));
    updateData({
      calendar: { ...data.calendar, birthdays: [...otherScope, ...tagged] }
    });
  };

  const enrichedBirthdays = useMemo(() => {
    return birthdays.map((person) => {
      const month = Number(person.month);
      const day = Number(person.day);
      const year = person.year ? Number(person.year) : null;

      let nextBirthday = new Date(currentYear, month - 1, day);
      nextBirthday.setHours(0, 0, 0, 0);
      if (nextBirthday < today) {
        nextBirthday = new Date(currentYear + 1, month - 1, day);
        nextBirthday.setHours(0, 0, 0, 0);
      }
      const daysUntil = Math.round((nextBirthday - today) / (1000 * 60 * 60 * 24));
      const nextAge = year ? nextBirthday.getFullYear() - year : null;
      const formattedDate = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}`;

      return {
        ...person,
        month,
        day,
        year,
        nextAge,
        ageOrdinal: nextAge ? getOrdinal(nextAge) : '',
        daysUntil,
        formattedDate
      };
    }).sort((a, b) => {
      if (a.daysUntil !== b.daysUntil) return a.daysUntil - b.daysUntil;
      return a.name.localeCompare(b.name);
    });
  }, [birthdays, currentYear, today]);

  // Group birthdays into ordered sections keyed by month+year of their next occurrence.
  // Already-passed birthdays roll over to next year, creating a separate (later) section
  // for the same month. Sort by daysUntil so the upcoming section appears before the
  // past section of the same month.
  const monthSections = useMemo(() => {
    const map = new Map();
    enrichedBirthdays.forEach((p) => {
      const nextYear = currentYear + (p.daysUntil >= 0 && new Date(currentYear, p.month - 1, p.day) >= today ? 0 : 1);
      const key = `${p.month}-${nextYear}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          monthName: monthNames[p.month - 1],
          year: nextYear,
          firstDaysUntil: p.daysUntil,
          people: []
        });
      }
      map.get(key).people.push(p);
    });
    // enrichedBirthdays is already sorted by daysUntil, so insertion order is correct
    return Array.from(map.values());
  }, [enrichedBirthdays, currentYear, today]);

  const buildPersonPayload = (form, id) => ({
    id: id || Date.now().toString(),
    name: form.name.trim(),
    day: Number(form.day),
    month: parseMonthInput(form.monthText),
    year: form.year !== '' && form.year != null ? Number(form.year) : null
  });

  const addPerson = () => {
    if (!validateForm(newPerson, currentYear)) return;
    const person = buildPersonPayload(newPerson);
    commitBirthdays([...rawBirthdays, person]);
    setNewPerson(emptyForm);
    setShowAddModal(false);
  };

  const updatePersonHandler = () => {
    if (!editingPerson || !validateForm(editingPerson, currentYear)) return;
    const updated = rawBirthdays.map((p) => {
      if (p.id !== editingPerson.id) return p;
      const payload = buildPersonPayload(editingPerson, p.id);
      // eslint-disable-next-line no-unused-vars
      const { birthDate, ...rest } = p;
      return { ...rest, ...payload };
    });
    commitBirthdays(updated);
    setEditingPerson(null);
  };

  const deletePerson = (id) => {
    commitBirthdays(rawBirthdays.filter((p) => p.id !== id));
    setEditingPerson(null);
  };

  return (
    <div className="space-y-6">
      {onBack && (
        <button
          onClick={onBack}
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          ← {sharedCal ? sharedCal.name : 'Back'}
        </button>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
          {sharedCal ? `${sharedCal.name} — Birthdays` : 'Birthdays'}
        </h1>
        <button
          onClick={() => { setNewPerson(emptyForm); setShowAddModal(true); }}
          className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 !px-3 !py-2 sm:!px-4 rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Add Person</span>
        </button>
      </div>

      {/* Calendar Link Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Show in Calendar</span>
          </div>
          <button
            onClick={() => updateSettings({ linkedToCalendar: !birthdaySettings.linkedToCalendar })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              birthdaySettings.linkedToCalendar ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                birthdaySettings.linkedToCalendar ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {birthdaySettings.linkedToCalendar && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Label Color</span>
              <div className="relative">
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
                >
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedCategory?.color }}
                  />
                  <span className="font-medium" style={{ color: selectedCategory?.color }}>
                    Birthday
                  </span>
                  <ChevronDown size={14} className="text-gray-400" />
                </button>
                {showColorPicker && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2 z-20 w-48">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          updateSettings({ categoryId: cat.id });
                          setShowColorPicker(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors ${
                          cat.id === birthdaySettings.categoryId ? 'bg-gray-100 font-medium' : ''
                        }`}
                      >
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span style={{ color: cat.color }}>{cat.name}</span>
                        {cat.id === birthdaySettings.categoryId && (
                          <span className="ml-auto text-blue-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Birthdays will appear in the calendar with a 🎁 icon and the selected color.
            </p>
          </div>
        )}
      </div>

      {/* Birthday List */}
      <div className="space-y-4">
        {monthSections.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Gift size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No birthdays added yet</p>
            <p className="text-sm mt-1">Click "Add Person" to get started</p>
          </div>
        ) : (
          monthSections.map((section) => {
            const isNextYear = section.year > currentYear;
            return (
              <div key={section.key}>
                <h2 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-3 py-1.5 bg-gray-100 rounded-md flex items-center justify-between">
                  <span>{section.monthName}</span>
                  {isNextYear && (
                    <span className="text-[10px] font-medium text-gray-400 normal-case tracking-normal">
                      {section.year}
                    </span>
                  )}
                </h2>

                <div className="space-y-0.5">
                  {section.people.map((person) => (
                    <div
                      key={person.id}
                      onClick={() =>
                        setEditingPerson({
                          id: person.id,
                          name: person.name,
                          day: String(person.day),
                          monthText: monthNames[person.month - 1],
                          year: person.year ? String(person.year) : ''
                        })
                      }
                      className="flex items-center justify-between py-3 px-3 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{person.name}</p>
                        <p className="text-sm text-gray-500">
                          {person.year ? (
                            <>{person.nextAge}{person.ageOrdinal} Birthday</>
                          ) : (
                            <span className="text-gray-400">Birthday</span>
                          )}
                          {person.daysUntil === 0 ? (
                            <span className="text-green-600 font-medium ml-1">Today!</span>
                          ) : person.daysUntil === 1 ? (
                            <span className="text-blue-600 font-medium ml-1">Tomorrow</span>
                          ) : (
                            <span className="text-gray-400 ml-1">in {person.daysUntil} days</span>
                          )}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-2 flex-shrink-0">
                        <p className="font-medium text-gray-900">{person.formattedDate}</p>
                        <ChevronRight size={16} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Person Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Person</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewPerson(emptyForm);
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <PersonForm
              form={newPerson}
              setForm={setNewPerson}
              onSubmit={addPerson}
              onCancel={() => {
                setShowAddModal(false);
                setNewPerson(emptyForm);
              }}
              submitLabel="Add"
              currentYear={currentYear}
            />
          </div>
        </div>
      )}

      {/* Edit Person Modal */}
      {editingPerson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Person</h3>
              <button
                onClick={() => setEditingPerson(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <PersonForm
              form={editingPerson}
              setForm={setEditingPerson}
              onSubmit={updatePersonHandler}
              onDelete={() => deletePerson(editingPerson.id)}
              submitLabel="Save"
              currentYear={currentYear}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdayCalendar;
